/**
 * Utility functions for formatting numbers, quantities, and currencies.
 * Standardized across all screens and components.
 */

/**
 * Format a number according to Vietnamese locale standards (dots for thousands, commas for decimals).
 * Handles numbers, numeric strings, undefined, and null safely.
 *
 * @example
 * formatNumber(1500) => "1.500"
 * formatNumber("250000.5") => "250.000,5"
 * formatNumber(null) => "0"
 */
export function formatNumber(
  val: number | string | null | undefined,
  fallback: string = '0'
): string {
  if (val === null || val === undefined || val === '') {
    return fallback;
  }
  const num = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(num)) {
    return fallback;
  }
  return num.toLocaleString('vi-VN');
}

/**
 * Format a quantity with its unit and optional prefix/sign.
 *
 * @example
 * formatQty(1500, 'g') => "1.500 g"
 * formatQty(200, 'g', { sign: '-' }) => "-200 g"
 * formatQty(50, 'ml', { sign: '+' }) => "+50 ml"
 */
export function formatQty(
  val: number | string | null | undefined,
  unit?: string,
  options?: { sign?: '+' | '-' | '='; fallback?: string }
): string {
  const formatted = formatNumber(val, options?.fallback ?? '0');
  const signPrefix = options?.sign ? `${options.sign} ` : '';
  const unitSuffix = unit ? ` ${unit}` : '';
  return `${signPrefix}${formatted}${unitSuffix}`.trim();
}

/**
 * Format a variance number with an explicit '+' for positive differences,
 * or '-' for negative differences.
 *
 * @example
 * formatVariance(250, 'g') => "+250 g"
 * formatVariance(-100, 'ml') => "-100 ml"
 * formatVariance(0, 'g') => "0 g"
 */
export function formatVariance(
  val: number | string | null | undefined,
  unit?: string
): string {
  const num = typeof val === 'number' ? val : Number(val || 0);
  const formatted = formatNumber(Math.abs(num));
  const sign = num > 0 ? '+' : num < 0 ? '-' : '';
  const unitSuffix = unit ? ` ${unit}` : '';
  return `${sign}${formatted}${unitSuffix}`.trim();
}

/**
 * Format internal or English units into user-friendly Vietnamese label
 * @example
 * formatUnitDisplay('PORTION') => "Ly"
 * formatUnitDisplay('portion') => "Ly"
 * formatUnitDisplay('g') => "g"
 */
export function formatUnitDisplay(unit?: string | null): string {
  if (!unit) return '';
  const trimmed = String(unit).trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'portion') {
    return 'Ly';
  }
  return trimmed;
}

